import { useState, useEffect, useCallback, useMemo } from "react";
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from "lucide-react";
import { api } from "../../lib/api";

/**
 * Config Health Banner — Part 1 of the Admin Observability spec (Calvin,
 * 2026-02-28). Sits below the admin header and above the tab content.
 * Reads `/api/admin/health/config` on mount + on "Recheck" click; renders
 * green / yellow / red per the worst check level, and expands to a
 * detail list on click. Hides itself entirely when `overall === "ok"`
 * so the healthy state is silent (green = no banner).
 *
 * Out of scope: no auto-remediation, no email/SMS alerting. Visibility only.
 */

const LEVEL_STYLES = {
  critical: {
    bar: "border-l-[#E63946] bg-[#FEE2E2]/60",
    text: "text-[#7F1D1D]",
    icon: AlertCircle,
    label: "CRITICAL",
  },
  warn: {
    bar: "border-l-[#F59E0B] bg-[#FEF3C7]/70",
    text: "text-[#92400E]",
    icon: AlertTriangle,
    label: "WARNING",
  },
  ok: {
    bar: "border-l-[#3BB273] bg-[#DCFCE7]/50",
    text: "text-[#166534]",
    icon: CheckCircle2,
    label: "OK",
  },
  skipped: {
    bar: "border-l-[#9CA3AF] bg-[#F3F4F6]/60",
    text: "text-[#4B5563]",
    icon: AlertTriangle,
    label: "SKIPPED",
  },
};

const timeAgo = (iso) => {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
};

export const ConfigHealthBanner = () => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/health/config");
      setData(r.data);
    } catch {
      // If even the health-check itself fails, surface a synthetic
      // critical row so admins aren't left in the dark.
      setData({
        overall: "critical",
        checked_at: new Date().toISOString(),
        checks: [{
          id: "health_endpoint",
          label: "Config health endpoint",
          level: "critical",
          detail: "Could not reach /api/admin/health/config",
        }],
      });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const style = useMemo(
    () => LEVEL_STYLES[data?.overall || "ok"] || LEVEL_STYLES.ok,
    [data?.overall],
  );
  const Icon = style.icon;

  // Silent when everything's green — the point is to be low-noise until
  // something actually needs attention.
  if (!data || data.overall === "ok") return null;

  const criticalCount = (data.checks || []).filter((c) => c.level === "critical").length;
  const warnCount = (data.checks || []).filter((c) => c.level === "warn").length;
  const summary = [
    criticalCount > 0 && `${criticalCount} critical`,
    warnCount > 0 && `${warnCount} warning${warnCount > 1 ? "s" : ""}`,
  ].filter(Boolean).join(" · ");

  return (
    <div
      className="max-w-7xl mx-auto px-6 pt-4"
      data-testid="config-health-banner"
    >
      <div
        className={`rounded-lg border-l-4 ${style.bar} p-3`}
        data-testid={`config-health-banner-${data.overall}`}
      >
        <div className="flex items-start gap-3">
          <Icon size={18} className={`${style.text} mt-0.5 shrink-0`} />
          <div className="flex-1 min-w-0">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-2 text-left w-full"
              data-testid="config-health-toggle"
            >
              <div className={`text-sm ${style.text}`}>
                <strong>Config health: {style.label}</strong>
                {summary && <span className="ml-2 opacity-80">· {summary}</span>}
              </div>
              {expanded ? (
                <ChevronUp size={14} className={style.text} />
              ) : (
                <ChevronDown size={14} className={style.text} />
              )}
            </button>
            <div className={`text-[11px] ${style.text} opacity-75 mt-0.5`}>
              Last checked {timeAgo(data.checked_at)}
            </div>
            {expanded && (
              <ul className="mt-3 space-y-1.5" data-testid="config-health-details">
                {(data.checks || []).map((c) => {
                  const cs = LEVEL_STYLES[c.level] || LEVEL_STYLES.ok;
                  const CIcon = cs.icon;
                  return (
                    <li
                      key={c.id}
                      className="flex items-start gap-2 text-[13px]"
                      data-testid={`config-health-check-${c.id}`}
                    >
                      <CIcon size={12} className={`${cs.text} mt-0.5 shrink-0`} />
                      <div>
                        <span className={`font-medium ${cs.text}`}>
                          {c.label}
                        </span>
                        <span className="text-[#374151] ml-2">
                          {c.detail}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={load}
            disabled={busy}
            data-testid="config-health-recheck"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-white ${style.text} border border-current/20 hover:bg-white/70 disabled:opacity-50 whitespace-nowrap`}
          >
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
            {busy ? "Checking…" : "Recheck"}
          </button>
        </div>
      </div>
    </div>
  );
};
